import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, DemoBadge, Notice } from "@/components/Badges";
import { KIND_LABELS, LOCKED_STATUSES } from "@/lib/format";
import SectionEditor from "@/components/application/SectionEditor";
import BudgetCalculator from "@/components/application/BudgetCalculator";
import ChecksPanel from "@/components/application/ChecksPanel";
import VersionsPanel from "@/components/application/VersionsPanel";
import ReviewApprove from "@/components/application/ReviewApprove";
import SubmissionPanel from "@/components/application/SubmissionPanel";
import StageTracker from "@/components/application/StageTracker";

export default function ApplicationDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [sources, setSources] = useState([]);
  const [tab, setTab] = useState("draft");
  const load = useCallback(async () => {
    const [a, s] = await Promise.all([api.get(`/applications/${id}`), api.get(`/applications/${id}/evidence-sources`)]);
    setApp(a.data);
    setSources(s.data);
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  if (!app) return <p className="text-sm text-slate-500">Loading…</p>;
  const locked = LOCKED_STATUSES.includes(app.status) || app.attempts.some((a) => ["in_progress", "uncertain"].includes(a.status));
  const tabs = [["draft", "Draft"], ...(app.kind === "grant" ? [["budget", "Budget"]] : []), ["checks", "Documents & checks"], ["versions", "Versions"], ["review", "Review & approve"], ["submit", "Submission & tracking"]];
  return (
    <div className="space-y-6 fade-up">
      <Link to={`/app/projects/${app.project_id}`} className="text-sm text-[#0D9488] hover:underline">← {app.project_title}</Link>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2"><span className="caption">{KIND_LABELS[app.kind]}</span>{app.is_demo && <DemoBadge text="Uses demonstration data" />}</div>
          <h1 data-testid="application-title" className="mt-1 text-2xl font-bold text-[#0B192C] sm:text-3xl">{app.title}</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">{app.template.description}</p>
        </div>
        <div className="self-start"><StatusBadge status={app.status} kind={app.kind} /></div>
      </div>
      {app.kind === "copyright_record" && <Notice tone="warn" testid="copyright-disclaimer">This is a private record only. It is not an official registration and is not proof that an enforceable right exists. The UK has no copyright register.</Notice>}
      {app.kind === "patent" && <Notice tone="warn">Keep this disclosure confidential. Professional review by a registered patent attorney is required before any specification or claims are filed.</Notice>}
      {locked && <Notice tone="info" testid="locked-notice">Content is locked because submission has started or completed. Tracking details remain editable.</Notice>}
      <StageTracker app={app} setTab={(t) => setTab(tabs.some(([k]) => k === t) ? t : "checks")} />
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200" role="tablist">
        {tabs.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`app-tab-${k}`} className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-[#0D9488] text-[#0B192C]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {l}{k === "checks" && app.checks.blocking.length > 0 && <span className="ml-1.5 rounded-full bg-red-100 px-1.5 text-[10px] text-red-700">{app.checks.blocking.length}</span>}
          </button>
        ))}
      </div>
      {tab === "draft" && <div className="space-y-4">{app.sections.map((s) => <SectionEditor key={s.key} app={app} section={s} sources={sources} locked={locked} reload={load} />)}</div>}
      {tab === "budget" && <BudgetCalculator app={app} sources={sources} locked={locked} reload={load} />}
      {tab === "checks" && <ChecksPanel app={app} sources={sources} locked={locked} reload={load} />}
      {tab === "versions" && <VersionsPanel app={app} />}
      {tab === "review" && <ReviewApprove app={app} reload={load} />}
      {tab === "submit" && <SubmissionPanel app={app} reload={load} />}
    </div>
  );
}
